import React from 'react';

// The Home Audio complaints board shows ONLY the buckets and per-bucket actions from the
// workbook "Bucket and Action Movement - Home Audio" (CS + WH sheets), Tushar, 22 Sep 2026.
// Buckets are enforced server-side (the strip); actions are enforced here by filtering the
// legacy dropdown, which is 8,000 lines of per-bucket JSX keyed on visible labels. Matching on
// the label is therefore the faithful mapping: the sheet is written in those same labels.

// Lower-case letters and digits only, so "Selship Request", "Move to selfship" and
// "Not connected 3 attempts" all compare cleanly whatever the case or punctuation.
export const normalizeActionLabel = (label) =>
    String(label || '')
        .toLowerCase()
        .replace(/selship/g, 'selfship')
        .replace(/successfull/g, 'successful')
        .replace(/renerate/g, 'regenerate')
        .replace(/unservicable/g, 'unserviceable')
        .replace(/[^a-z0-9]/g, '');

// Available from EVERY bucket, whatever the sheet lists for that row. Two groups:
//   - utilities that are not bucket movements at all;
//   - Close Complaint and Call Reminder, which the workbook omits on most rows but CS needs
//     everywhere, exactly as they behave on the normal complaints tab (Tushar, 24 Sep 2026).
const ALWAYS = [
    'Payment History', 'Edit', 'Save', 'Save Ticket', 'Ticket details', 'Flag', 'Print option', 'Reverse Pickup Print',
    'Close Complaint', 'Duplicate Close Complaint', 'Call Reminder',
];

// Sheet vocabulary -> the labels the legacy dropdown actually renders for the same action.
const SYNONYMS = {
    'Reverse Pickup Arranged': ['RPU', 'Re-arrange Reverse Pickup', 'Reverse pickup'],
    'Re-arrange reverse pickup': ['Re-arrange Reverse Pickup', 'RPU'],
    'Reverse Pickup Cancelled': ['Reverse pickup cancelled'],
    'Selfship Request': ['Move to selfship'],
    'Move to Selfship': ['Move to selfship'],
    'Close complaint': ['Close Complaint', 'Duplicate Close Complaint'],
    'policy closure': ['Policy Closure'],
    'Not Connected 3 attempts': ['Not connected 3 attempts'],
    'GC case': ['GC Case'],
    'call reminder': ['Call Reminder'],
    'Call': ['Call'],
    'Freshdesk': ['Freshdesk', 'FreshDesk'],
    'direct dispatch': ['Direct Dispatch', 'Direct dispatch'],
    'Warranty expired': ['Warranty Expired'],
    'Invalid invoice': ['Invalid Invoice'],
    'TS Successful by Agent': ['TS successfull by Agent'],
    'Product Pickedup': ['Product picked up', 'Product pickup'],
    'OTP Verification Failed': ['OTP verification failed'],
    'Selfship Declined. Close Ticket': ['Selfship declined. Close ticket.'],
    'Regenerate Payment Link': ['Regenerate payment link'],
    'Payment done': ['Payment done', 'Payment Done'],
    'Customer not paying': ['Customer not paying'],
    'Insurance Claim': ['Insurance Claim'],
    'Revoke Ticket': ['Revoke Ticket'],
    'Wrong Product Received': ['Wrong product received'],
    'Empty Product Received': ['Empty Box received'],
    'Reverse Pickup Received': ['Reverse pickup received'],
    'Request Payment': ['Request payment'],
    'GC Approval': ['GC Approval', 'GC Approve'],
    'Refund Approval': ['Refund Approval'],
    'Product Received': ['Product received'],
    'Transfer to Testing Inward': ['Transfer to testing inward'],
    'Moved to dispatch outward': ['Transfer to Dispatch Outward', 'Transfer to dispatch outward'],
    'Forward pickup print': ['Forward Pickup Print'],
    'Forward Pickup Cancelled': ['Forward pickup cancelled', 'Forward pickup cancel'],
    'Unserviceable Ticket': ['Unserviceable Ticket'],
    'RTO Out': ['RTO Out'],
    'Re-Dispatch': ['Re-dispatch'],
    'Installation Assigned to service partner': ['Installation Assigned to Service Partner'],
    'Service assigned to service partner': ['Service Assigned to Service Partner'],
    'parts used': ['Parts Used'],
};

const expand = (sheetLabels) => {
    const out = new Set();
    sheetLabels.forEach((label) => {
        out.add(normalizeActionLabel(label));
        (SYNONYMS[label] || []).forEach((alt) => out.add(normalizeActionLabel(alt)));
    });
    ALWAYS.forEach((label) => out.add(normalizeActionLabel(label)));
    return out;
};

const TICKET_RECEIVED_ACTIONS = [
    'Troubleshoot', 'Invalid invoice', 'Warranty expired', 'Reverse Pickup Arranged', 'Reverse Pickup Cancelled', 'Selfship Request',
    'Freshdesk', 'policy closure', 'Close complaint', 'Not Connected 3 attempts', 'GC case', 'Refund', 'call reminder', 'direct dispatch',
    'Revoke Ticket', 'Call', 'Installation Assigned to service partner', 'Service assigned to service partner', 'parts used',
];
const TROUBLESHOOT_ACTIONS = [
    'TS Successful by Agent', 'direct dispatch', 'Reverse Pickup Arranged', 'Reverse Pickup Cancelled', 'Selfship Request', 'Freshdesk',
    'policy closure', 'Close complaint', 'Not Connected 3 attempts', 'GC case', 'Refund', 'call reminder', 'Revoke Ticket',
    'Installation Assigned to service partner', 'Call', 'Service assigned to service partner', 'parts used',
];
const RTO_ACTIONS = [
    'Call', 'call reminder', 'RTO Out', 'Re-Dispatch', 'Close complaint', 'Not Connected 3 attempts', 'policy closure',
    'Unserviceable Ticket', 'GC case', 'Refund',
];

// Bucket id -> the sheet's action list. A missing key means "same as current movement": the
// dropdown is left untouched for that bucket. An empty list means "No Action".
export const HOME_AUDIO_BUCKET_ACTIONS = {
    // CS sheet
    1: TICKET_RECEIVED_ACTIONS,
    44: TICKET_RECEIVED_ACTIONS,
    3: TROUBLESHOOT_ACTIONS,
    4: TROUBLESHOOT_ACTIONS,
    22: ['Call', 'call reminder', 'Move to Selfship', 'GC case', 'Refund', 'Reverse Pickup Arranged', 'Reverse Pickup Cancelled', 'Close complaint',
        'Not Connected 3 attempts', 'policy closure', 'Wrong Product Received', 'Empty Product Received', 'Reverse Pickup Received', 'Troubleshoot',
        'Request Payment', 'direct dispatch', 'GC Approval', 'Refund Approval', 'Cosmos'],
    5: ['Re-arrange reverse pickup', 'Call', 'Revoke Ticket', 'Product Pickedup', 'Reverse Pickup Cancelled', 'OTP Verification Failed', 'Close complaint',
        'Not Connected 3 attempts', 'Freshdesk', 'policy closure', 'call reminder', 'GC case', 'Refund', 'Move to Selfship'],
    6: ['Re-arrange reverse pickup', 'Call', 'Close complaint', 'OTP Verification Failed', 'Not Connected 3 attempts', 'Freshdesk', 'GC case', 'Refund',
        'call reminder', 'Product Pickedup'],
    7: ['Selfship Declined. Close Ticket', 'Re-arrange reverse pickup', 'Call', 'Close complaint', 'Not Connected 3 attempts', 'Freshdesk', 'GC case',
        'Refund', 'call reminder', 'Product Pickedup'],
    9: ['Regenerate Payment Link', 'Payment done', 'Call', 'Not Connected 3 attempts', 'GC case', 'Refund', 'Customer not paying', 'Close complaint',
        'call reminder', 'Insurance Claim'],
    11: ['Revoke Ticket'],
    43: ['Reverse Pickup Arranged', 'Call', 'Not Connected 3 attempts', 'call reminder', 'Close complaint'],
    // WH sheet
    12: ['Product Received', 'Freshdesk', 'Close complaint', 'GC case', 'Refund', 'call reminder', 'Call'],
    13: ['Screening', 'Transfer to Testing Inward', 'Close complaint', 'Freshdesk', 'GC case', 'Refund', 'Call', 'call reminder'],
    14: ['Transfer to Testing Inward', 'Close complaint', 'Freshdesk', 'GC case', 'Refund', 'Call', 'call reminder', 'GC Approval', 'Refund Approval'],
    // 15 Testing Inward: the sheet says Call / Call Reminder only, but QC Pass, QC Fail and
    // Repair Payment are how a unit actually leaves this bucket, so it keeps the normal
    // complaint menu (Tushar, 22 Sep 2026).
    17: ['Moved to dispatch outward'],
    18: ['Forward pickup print', 'Unserviceable Ticket', 'Call', 'call reminder', 'Forward Pickup Cancelled'],
    19: RTO_ACTIONS,
    20: ['Re-Dispatch', 'Close complaint', 'Call', 'call reminder'],
    21: RTO_ACTIONS,
    // 35 Out of Stock: the sheet says "No Action", but Back to Stock is the only way out of
    // it, so it keeps the normal menu too (same decision as 15).
    // 10, 15, 35, 47, 48, 52, 41, 42: same as the normal complaints tab — untouched.
};

// The "All buckets" entry on the Home Audio board's bucket dropdown. 0 is what the list API
// already treats as "every bucket" (getTempBucketIds returns all ids for 0), so this needs no
// server change — it only has to be offered.
export const ALL_BUCKETS_OPTION = { id: 0, name: "All buckets" };

// With no single bucket selected there is no per-bucket action list to apply, so the dropdown
// falls back to the normal complaint menu for whatever bucket each ticket is actually in.
export const isAllBuckets = (bucketId) => Number(bucketId) === ALL_BUCKETS_OPTION.id;

const ALLOWED_CACHE = {};
const allowedFor = (bucketId) => {
    const key = Number(bucketId);
    if (!(key in HOME_AUDIO_BUCKET_ACTIONS)) return null;
    if (!ALLOWED_CACHE[key]) ALLOWED_CACHE[key] = expand(HOME_AUDIO_BUCKET_ACTIONS[key]);
    return ALLOWED_CACHE[key];
};

export const isHomeAudioActionAllowed = (bucketId, label) => {
    const allowed = allowedFor(bucketId);
    return allowed === null ? true : allowed.has(normalizeActionLabel(label));
};

const textOf = (node) => {
    if (node === null || node === undefined || typeof node === 'boolean') return '';
    if (typeof node === 'string' || typeof node === 'number') return String(node);
    if (Array.isArray(node)) return node.map(textOf).join(' ');
    if (React.isValidElement(node)) return textOf(node.props.children);
    return '';
};

const isDropdownItem = (node) => React.isValidElement(node) && typeof node.type === 'string' && /\bdropdown-item\b/.test(String(node.props.className || ''));

// Walks a rendered tree and drops every `.dropdown-item` whose visible text the sheet does
// not allow for this bucket. Buckets the sheet marks "same as current movement" pass through.
export const filterHomeAudioActions = (tree, bucketId) => {
    if (allowedFor(bucketId) === null) return tree;
    const walk = (node, index) => {
        if (Array.isArray(node)) return node.map((child, i) => walk(child, i));
        if (!React.isValidElement(node)) return node;
        if (isDropdownItem(node) && !isHomeAudioActionAllowed(bucketId, textOf(node))) return null;

        const children = node.props.children;
        // Children go through the props object, not cloneElement's variadic third argument:
        // the variadic form makes React re-validate keys on an array it considers freshly
        // created, which warns on children the original JSX never needed keys for.
        const next = children === undefined || children === null ? node : React.cloneElement(node, { children: walk(children) });
        // An element that came out of an array and had no key of its own gets a stable one,
        // so filtering a list cannot make React re-mount the survivors.
        return index === undefined || next.key !== null ? next : React.cloneElement(next, { key: `ha-act-${index}` });
    };
    return walk(tree);
};
